// The missed check-in alerts feed (architecture sprint-14 §8.5). Polled every 30 s while the viewer holds
// checkinAlerts; in background tabs too when they get browser notifications (checkinNotify), so the OS
// notification arrives while Clockit is not the active tab. Silent: badges simply stay as they were on error.
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { LIVE_REFETCH_MS } from '@shared/report-rules';
import type { CheckinAlertsDto } from '@shared/alarm-rules';
import { useMe } from '@/hooks/use-me';
import { apiRequest } from '@/lib/api';

export const CHECKIN_ALERTS_KEY = ['oversight', 'checkin-alerts'] as const;

export function useCheckinAlerts(): UseQueryResult<CheckinAlertsDto> {
  const { data: me } = useMe();
  const enabled = me?.access.checkinAlerts === true;
  return useQuery({
    queryKey: CHECKIN_ALERTS_KEY,
    queryFn: () => apiRequest<CheckinAlertsDto>('/api/oversight/checkin-alerts'),
    enabled,
    meta: { silent: true },
    refetchInterval: LIVE_REFETCH_MS,
    refetchIntervalInBackground: me?.access.checkinNotify === true,
    refetchOnWindowFocus: true,
  });
}

/** The badge counts: the total and per project (0 when the feed is off or not loaded). */
export function useCheckinAlertCounts(): { total: number; byProject: Map<string, number> } {
  const { data } = useCheckinAlerts();
  return { total: data?.total ?? 0, byProject: new Map((data?.projects ?? []).map((p) => [p.projectId, p.count])) };
}
